import { Icon } from '@/components';
import { cn } from '@/lib/cn';
import type { WorkflowRunFailureDetail } from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';

/**
 * Expandable "why did this run fail" panel for a workflow-run row on the
 * release detail page: summary, failed jobs/steps, GitHub annotations, and
 * the captured log tail.
 */
export const RunFailurePanel = (props: { detail: WorkflowRunFailureDetail }) => {
  const [open, setOpen] = createSignal(false);
  return (
    <div class="mt-1">
      <button
        type="button"
        class="flex items-center gap-1 text-xs text-red-700 hover:underline dark:text-red-400"
        aria-expanded={open()}
        onClick={() => setOpen(!open())}
      >
        <Icon
          name="angle-small-down"
          class={cn(
            'shrink-0 transition-transform duration-200 motion-reduce:transition-none',
            !open() && '-rotate-90',
          )}
        />
        {props.detail.summary}
      </button>
      {/* Expand/collapse via the CSS grid-rows trick so height animates without JS measuring. */}
      <div
        inert={!open()}
        class={cn(
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
          open() ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div class="overflow-hidden">
          <div class="mt-2 flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs dark:border-red-900 dark:bg-red-950/40">
            <Show
              when={props.detail.jobs.length > 0}
              fallback={
                <p class="text-neutral-600 dark:text-neutral-300">No job details captured.</p>
              }
            >
              <For each={props.detail.jobs}>
                {(job) => (
                  <div class="flex flex-col gap-1.5">
                    <p class="font-medium">
                      <Show when={job.htmlUrl} fallback={job.name}>
                        <a
                          href={job.htmlUrl}
                          target="_blank"
                          rel="noreferrer"
                          class="text-brand-600 hover:underline dark:text-brand-400"
                        >
                          {job.name}
                        </a>
                      </Show>
                      <Show when={job.failedSteps.length > 0}>
                        <span class="font-normal text-neutral-600 dark:text-neutral-300">
                          {' '}
                          — failed steps: {job.failedSteps.join(', ')}
                        </span>
                      </Show>
                    </p>
                    <Show when={job.annotations.length > 0}>
                      <ul class="flex flex-col gap-0.5">
                        <For each={job.annotations}>
                          {(a) => (
                            <li class="text-red-700 dark:text-red-400">
                              [{a.level}] {a.message}
                            </li>
                          )}
                        </For>
                      </ul>
                    </Show>
                    <Show when={job.logTail}>
                      <pre class="max-h-64 overflow-auto rounded-md bg-neutral-900 p-2 font-mono text-[11px] leading-relaxed text-neutral-100">
                        {job.logTail}
                      </pre>
                    </Show>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
};
